import _ from 'lodash';
import { IInstructionItemJson } from '@scale/shared/types/instructionitem';
import { ErrorSeverityLevel, PerAnnotationError } from '@scale/shared/types/customerAudit';
import { InstructionItemSummaryData } from 'react-scale/components/benchmarks/InstructionItemSummary';

export type InstrItemTags = {
  taskInstructionItems: string[];
  perAnnotationInstructionItems: PerAnnotationError[];
};

export enum ModifyInstrItemTagsMode {
  Add = 1,
  Delete = -1,
}

// Called by modifyInstrItemTags when selectedAnnotation exists.
// Returns the new perAnnotationError array, modified from the one in props.
export const modifyAnnotationInstrItemTags = (
  mode: ModifyInstrItemTagsMode,
  originalErrors: PerAnnotationError[],
  selectedInstrItems: string[],
  selectedAnnotation: string,
): PerAnnotationError[] => {
  const newAnnotationErrors = (originalErrors ?? []).slice(0);
  const matchingBinIndex = _.findIndex(
    newAnnotationErrors,
    error => error.annotationId === selectedAnnotation,
  );
  // Return early if you try to delete from a bin that doesn't exist.
  if (matchingBinIndex < 0 && mode === ModifyInstrItemTagsMode.Delete) {
    return newAnnotationErrors;
  }
  // Create empty bin if it doesn't exist.
  const binToOperateOn =
    matchingBinIndex >= 0
      ? newAnnotationErrors[matchingBinIndex]
      : {
          annotationId: selectedAnnotation,
          severity: ErrorSeverityLevel.Default,
          errorType: '',
          comment: '',
          instructionItems: [],
        };
  switch (mode) {
    case ModifyInstrItemTagsMode.Add: {
      binToOperateOn.instructionItems = _.union(
        binToOperateOn.instructionItems,
        selectedInstrItems,
      );
      break;
    }
    case ModifyInstrItemTagsMode.Delete: {
      binToOperateOn.instructionItems = _.pullAll(
        binToOperateOn.instructionItems ?? [],
        selectedInstrItems,
      );
      // Delete the bin if it is empty. Matching bin index is guaranteed to be >= 0.
      if (binToOperateOn.instructionItems.length === 0) {
        newAnnotationErrors.splice(matchingBinIndex, 1);
      }
      break;
    }
  }
  // Lastly, if bin was freshly created, add it to the newAnnotationErrors. (guaranteed to be an Add operation)
  if (matchingBinIndex < 0) {
    newAnnotationErrors.push(binToOperateOn);
  }
  return newAnnotationErrors;
};

// Called by modifyInstrItemTags when selectedAnnotation DNE.
// Returns the new taskInstructionItems array, modified from the one in props.
export const modifyTaskInstrItems = (
  mode: ModifyInstrItemTagsMode,
  originalInstrItems: string[],
  selectedInstrItems: string[],
): string[] => {
  let currentInstrItems = (originalInstrItems ?? []).slice(0);
  switch (mode) {
    case ModifyInstrItemTagsMode.Add: {
      currentInstrItems = _.union(currentInstrItems, selectedInstrItems);
      break;
    }
    case ModifyInstrItemTagsMode.Delete: {
      currentInstrItems = _.pullAll(currentInstrItems, selectedInstrItems);
      break;
    }
  }
  return currentInstrItems;
};

// Ingests instrItemData, used for InstructionItemSummary
export const instrItemSummaryGenerator = (
  instrItemTags: InstrItemTags,
  projectInstrItems: IInstructionItemJson[],
): InstructionItemSummaryData => {
  const summary: InstructionItemSummaryData = {};
  instrItemTags.taskInstructionItems.forEach(id => {
    const bin = summary[id] ?? {
      annotations: [] as string[],
      instrItem: projectInstrItems.find(instrItem => instrItem._id === id) as IInstructionItemJson,
      count: 0,
    };
    bin.annotations.push('Task-Level');
    bin.count = bin.annotations.length;
    summary[id] = bin;
  });
  instrItemTags.perAnnotationInstructionItems.forEach(perAnnoError => {
    perAnnoError.instructionItems?.forEach(instrItemId => {
      const bin = summary[instrItemId] ?? {
        annotations: [] as string[],
        instrItem: projectInstrItems.find(
          instrItem => instrItem._id === instrItemId,
        ) as IInstructionItemJson,
        count: 0,
      };
      bin.annotations.push(perAnnoError.annotationId);
      bin.count = bin.annotations.length;
      summary[instrItemId] = bin;
    });
  });
  return summary;
};

export const isAnnotationIsCurrentlyTagged = (
  annotId: string,
  annotErrors: PerAnnotationError[] | undefined,
): boolean => {
  const index = _.findIndex(annotErrors, annot => annot.annotationId === annotId);
  return index >= 0;
};
